// Tab mở từ trước một lần deploy: chunk JS/CSS có hash cũ không còn trên server (nginx trả 404 cho /assets/* không có) ⇒ mở
// trang / lớp phủ chưa nạp thì import động thất bại. Error boundary gốc (app/router) và của từng lớp phủ (OverlayHost) gọi
// useStaleChunkReload: lỗi kiểu này ⇒ tải lại trang (giữ URL) để lấy index.html mới thay cho báo lỗi. Chặn lặp bằng dấu thời
// điểm trong sessionStorage: vừa tải lại vì lý do này chưa tới 1 phút mà vẫn lỗi (mạng hỏng thật, server thiếu file) ⇒ để lỗi
// hiện như mọi lỗi khác. Lỗi mạng tạm / 502 lúc thay container web cũng khớp ⇒ tải lại vào trang lỗi của trình duyệt (một lần,
// nhờ dấu chặn): chấp nhận, cửa sổ đó chỉ vài giây khi deploy.
// Không tải lại trong sự kiện `vite:preloadError`: Vite phát nó cho mọi import động lỗi (cả khi mã của module ném lúc chạy) và
// cả lúc router nạp trước khi rê chuột / lớp phủ nạp lúc rảnh — tải lại khi đó làm mất việc đang dở. Ở đây chỉ tải lại khi lỗi
// sắp hiện ra cho người dùng.
import { useEffect, useState } from 'react';

const KEY = 'cathouse.chunkReload';
const RETRY_AFTER_MS = 60_000;
// Chrome/Edge: "Failed to fetch dynamically imported module: <url>"; Firefox: "error loading dynamically imported module: <url>";
// Safari: "Importing a module script failed."; Vite (CSS đi kèm chunk): "Unable to preload CSS for <url>".
const STALE_CHUNK = /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS for/i;

/** Import động lỗi vì không tải được file chunk (khác lỗi do mã của module ném khi chạy). */
export function isStaleChunkError(e: unknown): boolean {
  const message = (e as { message?: unknown } | null | undefined)?.message;
  return typeof message === 'string' && STALE_CHUNK.test(message);
}

/** Lỗi chunk cũ, đang có mạng, chưa tải lại vì lý do này trong 1 phút. Chỉ đọc; không đọc được dấu ⇒ không tải lại. */
function shouldReload(e: unknown): boolean {
  if (!isStaleChunkError(e) || !navigator.onLine) return false;
  try {
    const since = Date.now() - Number(sessionStorage.getItem(KEY) ?? 0);
    // Đồng hồ lùi sau lần tải lại (dấu ở tương lai) hay dấu hỏng (NaN) ⇒ không coi là vừa tải lại
    return !(since >= 0 && since < RETRY_AFTER_MS);
  } catch {
    return false;
  }
}

/** Ghi dấu rồi tải lại trang (giữ URL). Không ghi được dấu ⇒ không tải lại (có thể lặp vô hạn), trả false. */
function reloadPage(): boolean {
  try {
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch {
    return false;
  }
  location.reload();
  return true;
}

/** Cho error boundary: lỗi chunk cũ ⇒ tải lại trang, trả true (hiện "đang tải" thay cho báo lỗi); lỗi khác ⇒ false. */
export function useStaleChunkReload(error: unknown): boolean {
  const [reloading, setReloading] = useState(() => shouldReload(error));
  useEffect(() => {
    if (reloading && !reloadPage()) setReloading(false);
  }, [reloading]);
  return reloading;
}
